import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Archive, CalendarDays, CheckCircle2, ChevronRight, Clock3, ExternalLink, File as FileIcon,
  FileText, FolderOpen, Globe, LayoutGrid, MessageSquare, MoreHorizontal, Paperclip,
  RefreshCcw, Smartphone, Upload,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { WEB_OP_STATUSES, webStageLabel, webTypeLabel, webCmsLabel } from "@/lib/constants";
import { toast } from "sonner";

const fmtD = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; }
};
const fmtDs = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; }
};
const fmtDT = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }); } catch { return ""; }
};
const relTime = (iso) => {
  if (!iso) return "";
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return "à l'instant";
  if (h < 24) return `il y a ${h} heure${h > 1 ? "s" : ""}`;
  const d = Math.floor(h / 24);
  return `il y a ${d} jour${d > 1 ? "s" : ""}`;
};
const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const eur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

export const STEPS = ["Arborescence", "Maquette", "Contenus", "Développement", "SEO", "Validation", "Mise en ligne"];
const STAGE_STEP = {
  creation: 0, cahier_des_charges: 0, design: 1, collecte: 2, developpement: 3, seo: 4,
  tests: 5, validation_finale: 5, prete_mise_en_ligne: 6, en_ligne: 6, recette: 6, cloture: 6,
};

export const pageStatus = (pg) => {
  if (pg.status_validation === "valide" || pg.status_dev === "termine") return { label: "Terminée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (pg.status_validation === "envoyee") return { label: "À valider", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  if (pg.status_dev === "en_cours" || pg.status_design === "en_cours" || pg.status_content === "recu") return { label: "En cours", cls: "bg-blue-50 text-blue-700 border-blue-200" };
  return { label: "À venir", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" };
};

export const opBadge = (op, stage) => {
  if (stage === "en_ligne" || stage === "recette" || stage === "cloture" || op === "termine") return { label: "Finalisé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (op === "attente_validation") return { label: "À valider", cls: "bg-rose-50 text-rose-600 border-rose-200" };
  if (["attente_client", "attente_webmaster", "bloque", "en_retard"].includes(op)) return { label: "En review", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "En cours", cls: "bg-blue-50 text-blue-700 border-blue-200" };
};

const ASSET_KIND_LABELS = { moodboard: "Moodboard", inspiration: "Inspiration", palette: "Palette", typo: "Typographie", maquette: "Maquette", autre: "Fichier" };

export default function WebProjectPanel({ detail: p, tab, setTab, onChanged }) {
  const navigate = useNavigate();
  const [desc, setDesc] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgInternal, setMsgInternal] = useState(true);
  const [pageName, setPageName] = useState("");
  const descFor = useRef(null);

  const description = desc !== null && descFor.current === p?.id ? desc : (p?.description || "");

  const versions = useMemo(() => ((p?.design || {}).versions || []), [p]);
  const assets = useMemo(() => ((p?.design || {}).assets || []), [p]);
  const stepIdx = STAGE_STEP[p?.stage] ?? 0;

  if (!p) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex items-center justify-center min-h-0" data-testid="wpanel-loading">
        <p className="text-sm text-neutral-400">Sélectionnez un projet…</p>
      </div>
    );
  }

  const badge = opBadge(p.op_status, p.stage);
  const pages = p.pages || [];
  const pagesDone = pages.filter((pg) => pageStatus(pg).label === "Terminée").length;
  const deadlineDays = p.delivery_date ? Math.ceil((new Date(p.delivery_date + "T23:59:59") - Date.now()) / 86400000) : null;

  const patch = async (fields, msg) => {
    try {
      await api.patch(`/web/projects/${p.id}`, fields);
      if (msg) toast.success(msg);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveDesc = () => { if (desc !== null && desc !== (p.description || "")) patch({ description: desc }, "Description enregistrée"); };
  const archive = async () => {
    if (!window.confirm("Archiver ce projet web ?")) return;
    try { await api.post(`/web/projects/${p.id}/archive`, { archived: true }); toast.success("Projet archivé"); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addPage = async () => {
    if (!pageName.trim()) return;
    try {
      await api.post(`/web/projects/${p.id}/pages`, { name: pageName.trim() });
      setPageName("");
      toast.success("Page ajoutée à l'arborescence");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delPage = async (pgid, name) => {
    if (!window.confirm(`Supprimer la page « ${name} » ?`)) return;
    try { await api.delete(`/web/projects/${p.id}/pages/${pgid}`); toast.success("Page supprimée"); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const postMessage = async () => {
    if (!msgText.trim()) return;
    try {
      await api.post(`/web/projects/${p.id}/messages`, { text: msgText.trim(), internal: msgInternal });
      setMsgText("");
      toast.success(msgInternal ? "Note interne ajoutée" : "Message visible par le client publié");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex flex-col overflow-hidden min-h-0" data-testid="wpanel">
      {/* En-tête */}
      <div className="px-4 pt-4 shrink-0">
        <div className="flex items-start gap-3">
          {p.preview_url ? (
            <img src={p.preview_url} alt="" className="w-16 h-16 rounded-xl object-cover border border-neutral-100 shrink-0" data-testid="wpanel-thumb" />
          ) : (
            <span className="w-16 h-16 rounded-xl bg-[#E8F0FE] border border-neutral-100 flex items-center justify-center shrink-0" data-testid="wpanel-thumb"><Globe className="w-6 h-6 text-[#1D4ED8]" /></span>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-base text-[#0F172A] truncate" data-testid="wpanel-name">{p.name}</h3>
                <Link to={`/clients/${p.client_id}`} className="text-xs font-medium text-[#0369A1] hover:underline" data-testid="wpanel-client">{p.client_name} ›</Link>
                <p className="text-[10px] text-neutral-400 mt-0.5">{webTypeLabel(p.type)} · {webCmsLabel(p.cms)}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <select value={p.op_status} onChange={(e) => patch({ op_status: e.target.value }, "Statut mis à jour")} data-testid="wpanel-status"
                  className={`appearance-none cursor-pointer text-[10px] font-semibold border rounded-full pl-2.5 pr-2 py-1 focus:outline-none ${badge.cls}`}>
                  {WEB_OP_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <div className="relative">
                  <button onClick={() => setMenuOpen((v) => !v)} data-testid="wpanel-menu" className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 transition-colors">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                  {menuOpen && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                      <div className="absolute right-0 top-8 z-40 w-52 bg-white border border-neutral-200 rounded-xl shadow-lg py-1" data-testid="wpanel-menu-list">
                        <button onClick={() => navigate(`/sites-web/projet/${p.id}`)} data-testid="wpanel-action-open"
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50 transition-colors">
                          <FolderOpen className="w-3.5 h-3.5 text-neutral-400" /> Ouvrir le projet (page complète)
                        </button>
                        <button onClick={archive} data-testid="wpanel-action-archive"
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-red-600 hover:bg-red-50 transition-colors">
                          <Archive className="w-3.5 h-3.5" /> Archiver
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview du site (cadre navigateur) */}
        <div className="mt-3 rounded-xl border border-neutral-200 overflow-hidden bg-neutral-50 shrink-0" data-testid="wpanel-preview">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border-b border-neutral-100">
            <span className="w-2 h-2 rounded-full bg-[#FCA5A5]" /><span className="w-2 h-2 rounded-full bg-[#FCD34D]" /><span className="w-2 h-2 rounded-full bg-[#86EFAC]" />
            <span className="ml-2 flex-1 text-[9px] text-neutral-400 bg-neutral-50 border border-neutral-100 rounded-md px-2 py-0.5 truncate">
              {p.preprod_url || p.production_url || `preprod.${(p.client_name || "projet").toLowerCase().replace(/[^a-z]/g, "")}.ch`}
            </span>
          </div>
          {p.preview_url ? (
            <img src={p.preview_url} alt="" className="w-full h-32 object-cover object-top" />
          ) : (
            <div className="w-full h-32 flex items-center justify-center text-neutral-300"><Globe className="w-8 h-8" /></div>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="flex items-center gap-4 px-4 mt-2 border-b border-neutral-100 shrink-0 overflow-x-auto" data-testid="wpanel-tabs">
        {[["details", "Détails"], ["pages", "Pages"], ["fichiers", "Fichiers"], ["commentaires", "Commentaires"], ["historique", "Historique"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`wpanel-tab-${id}`}
            className={`pb-2 text-xs font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === id ? "border-[#0B132B] text-[#0B132B]" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-4">
        {tab === "details" && (
          <>
            <section>
              <h4 className="text-xs font-bold text-[#0F172A] mb-1">Description du projet</h4>
              <textarea value={description} rows={2} data-testid="wpanel-description"
                onChange={(e) => { descFor.current = p.id; setDesc(e.target.value); }}
                onBlur={saveDesc} placeholder="Brief du projet… (modifiable)"
                className="w-full text-xs text-neutral-600 leading-relaxed bg-transparent rounded-lg px-2 py-1.5 -ml-2 hover:bg-neutral-50 focus:bg-white focus:border focus:border-neutral-200 focus:outline-none resize-y transition-colors" />
            </section>

            <div className="grid grid-cols-3 gap-2" data-testid="wpanel-infos">
              <div className="border border-neutral-100 rounded-xl p-2.5">
                <span className="w-6 h-6 rounded-lg bg-[#DBEAFE] flex items-center justify-center mb-1.5"><RefreshCcw className="w-3 h-3 text-[#1D4ED8]" /></span>
                <p className="text-[10px] text-neutral-400">Avancement global</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden"><div className="h-full bg-[#3B82F6] rounded-full" style={{ width: `${p.progress}%` }} /></div>
                  <span className="text-xs font-bold text-[#0F172A]">{p.progress} %</span>
                </div>
              </div>
              <div className="border border-neutral-100 rounded-xl p-2.5">
                <span className="w-6 h-6 rounded-lg bg-[#E0E7FF] flex items-center justify-center mb-1.5"><LayoutGrid className="w-3 h-3 text-[#4338CA]" /></span>
                <p className="text-[10px] text-neutral-400">Étape actuelle</p>
                <p className="text-sm font-bold text-[#0F172A] truncate">{webStageLabel(p.stage)}</p>
                <p className="text-[9px] text-neutral-400">étape {stepIdx + 1} / {STEPS.length}</p>
              </div>
              <div className="border border-neutral-100 rounded-xl p-2.5">
                <span className="w-6 h-6 rounded-lg bg-[#FEF3E2] flex items-center justify-center mb-1.5"><CalendarDays className="w-3 h-3 text-[#B45309]" /></span>
                <p className="text-[10px] text-neutral-400">Date de livraison</p>
                <p className="text-sm font-bold text-[#0F172A]" data-testid="wpanel-deadline">{fmtDs(p.delivery_date)}</p>
                <p className="text-[9px] text-neutral-400">{deadlineDays != null ? (deadlineDays < 0 ? "En retard" : deadlineDays === 0 ? "Aujourd'hui" : `Dans ${deadlineDays} jours`) : "—"}</p>
              </div>
              <div className="border border-neutral-100 rounded-xl p-2.5">
                <span className="w-6 h-6 rounded-lg bg-[#DCFCE7] flex items-center justify-center mb-1.5"><CheckCircle2 className="w-3 h-3 text-[#15803D]" /></span>
                <p className="text-[10px] text-neutral-400">Validation client</p>
                <p className="text-sm font-bold text-[#0F172A]">{(p.validations || []).some((v) => v.status === "envoyee") || p.op_status === "attente_validation" ? "En attente" : "—"}</p>
                <p className="text-[9px] text-neutral-400">{p.elements_a_verifier > 0 ? `${p.elements_a_verifier} élément(s) à vérifier` : "à jour"}</p>
              </div>
              <div className="border border-neutral-100 rounded-xl p-2.5">
                <span className="w-6 h-6 rounded-lg bg-[#F1F5F9] flex items-center justify-center mb-1.5"><FileText className="w-3 h-3 text-[#64748B]" /></span>
                <p className="text-[10px] text-neutral-400">Nombre de pages</p>
                <p className="text-sm font-bold text-[#0F172A]">{pages.length} page{pages.length > 1 ? "s" : ""}</p>
                <p className="text-[9px] text-neutral-400">{pagesDone} terminée{pagesDone > 1 ? "s" : ""}</p>
              </div>
              <div className="border border-neutral-100 rounded-xl p-2.5">
                <span className="w-6 h-6 rounded-lg bg-[#FDEAE8] flex items-center justify-center mb-1.5"><Smartphone className="w-3 h-3 text-[#B91C1C]" /></span>
                <p className="text-[10px] text-neutral-400">Responsive</p>
                <p className="text-sm font-bold text-[#0F172A] truncate">Desktop · tablette · mobile</p>
                <p className="text-[9px] text-emerald-600">Optimisé</p>
              </div>
            </div>

            {/* Étapes */}
            <section data-testid="wpanel-steps">
              <h4 className="text-xs font-bold text-[#0F172A] mb-2 flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5 text-neutral-400" /> Étapes du projet</h4>
              <div className="flex items-start">
                {STEPS.map((label, i) => {
                  const state = i < stepIdx ? "done" : i === stepIdx ? "current" : "todo";
                  return (
                    <div key={label} className="flex-1 flex flex-col items-center relative" data-testid={`wpanel-step-${i}`}>
                      {i > 0 && <span className={`absolute top-[9px] right-1/2 w-full h-0.5 ${i <= stepIdx ? "bg-[#3B82F6]" : "bg-neutral-200"}`} />}
                      <span className={`relative z-10 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center ${state === "done" ? "bg-[#3B82F6] border-[#3B82F6]" : state === "current" ? "bg-white border-[#3B82F6]" : "bg-white border-neutral-300"}`}>
                        {state === "done" && <CheckCircle2 className="w-3 h-3 text-white" />}
                        {state === "current" && <span className="w-2 h-2 rounded-full bg-[#3B82F6]" />}
                      </span>
                      <span className={`mt-1 text-[9px] font-semibold text-center leading-tight ${state === "todo" ? "text-neutral-400" : "text-[#0F172A]"}`}>{label}</span>
                      <span className={`text-[8px] ${state === "current" ? "text-[#1D4ED8] font-semibold" : "text-neutral-300"}`}>
                        {state === "done" ? "Terminée" : state === "current" ? "En cours" : "À venir"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Fichiers */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <h4 className="text-xs font-bold text-[#0F172A] flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5 text-neutral-400" /> Fichiers du projet ({assets.length})</h4>
                <button onClick={() => setTab("fichiers")} className="ml-auto text-[10px] font-semibold text-[#0369A1] hover:underline" data-testid="wpanel-files-all">Voir tous les fichiers ›</button>
              </div>
              {assets.length === 0 && <p className="text-[11px] text-neutral-400">Aucun fichier pour l'instant.</p>}
              <div className="grid grid-cols-2 gap-2">
                {assets.slice(0, 4).map((a) => (
                  <a key={a.id} href={a.url || undefined} target="_blank" rel="noreferrer" data-testid={`wpanel-asset-${a.id}`}
                    className="border border-neutral-100 rounded-xl px-2.5 py-2 flex items-center gap-2 hover:border-neutral-300 transition-colors">
                    <span className="w-7 h-7 rounded-lg bg-[#FEF3E2] flex items-center justify-center shrink-0"><FileIcon className="w-3.5 h-3.5 text-[#B45309]" /></span>
                    <span className="min-w-0">
                      <span className="block text-[10px] font-semibold text-[#0F172A] truncate">{a.name}</span>
                      <span className="block text-[9px] text-neutral-400">{ASSET_KIND_LABELS[a.kind] || "Fichier"} · {fmtDT(a.at)}</span>
                    </span>
                  </a>
                ))}
              </div>
            </section>
          </>
        )}

        {tab === "pages" && (
          <>
            <div className="flex items-center gap-2">
              <input value={pageName} onChange={(e) => setPageName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPage()}
                placeholder="Nom de la page (ex. FAQ)…" data-testid="wpanel-page-input"
                className="flex-1 text-xs border border-neutral-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-neutral-400" />
              <button onClick={addPage} data-testid="wpanel-page-add"
                className="text-xs font-semibold bg-[#0B132B] text-white rounded-lg px-3 py-1.5 hover:bg-[#1C2A4F] transition-colors shrink-0">+ Ajouter</button>
            </div>
            {pages.length === 0 && <p className="text-[11px] text-neutral-400">Aucune page — ajoutez la première page du site.</p>}
            <div className="space-y-1">
              {pages.map((pg) => {
                const ps = pageStatus(pg);
                return (
                  <div key={pg.id} className="flex items-center gap-2 border-b border-neutral-50 last:border-0 py-1.5 group" data-testid={`wpanel-page-${pg.id}`}>
                    <FileText className="w-3.5 h-3.5 text-neutral-300 shrink-0" />
                    <span className="flex-1 min-w-0 text-xs font-medium text-[#0F172A] truncate">{pg.name}</span>
                    <span className={`text-[9px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${ps.cls}`}>{ps.label}</span>
                    <span className="text-[10px] text-neutral-400 w-16 text-right shrink-0">{pg.updated_at ? fmtDs(pg.updated_at) : "—"}</span>
                    <button onClick={() => delPage(pg.id, pg.name)} data-testid={`wpanel-page-del-${pg.id}`}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded text-neutral-300 hover:text-red-500 transition-all" title="Supprimer">×</button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "fichiers" && (
          <>
            {assets.length === 0 && versions.length === 0 && <p className="text-[11px] text-neutral-400">Aucun fichier ni maquette pour l'instant — la gestion complète se fait sur la page projet.</p>}
            {assets.length > 0 && (
              <section>
                <h4 className="text-xs font-bold text-[#0F172A] mb-2">Fichiers & liens</h4>
                <div className="space-y-1">
                  {assets.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 py-1 border-b border-neutral-50 last:border-0" data-testid={`wpanel-fasset-${a.id}`}>
                      <span className="w-6 h-6 rounded-md bg-[#FEF3E2] flex items-center justify-center shrink-0"><FileIcon className="w-3 h-3 text-[#B45309]" /></span>
                      <span className="flex-1 min-w-0 text-xs text-neutral-700 truncate">{a.name}</span>
                      <span className="text-[9px] text-neutral-400 border border-neutral-100 rounded-full px-1.5 py-px">{ASSET_KIND_LABELS[a.kind] || "Fichier"}</span>
                      <span className="text-[10px] text-neutral-400 shrink-0">{fmtDT(a.at)}</span>
                      {a.url && <a href={a.url} target="_blank" rel="noreferrer" className="p-1 text-neutral-300 hover:text-neutral-700" title="Ouvrir"><ExternalLink className="w-3 h-3" /></a>}
                    </div>
                  ))}
                </div>
              </section>
            )}
            {versions.length > 0 && (
              <section>
                <h4 className="text-xs font-bold text-[#0F172A] mb-2">Versions de maquette</h4>
                <div className="space-y-1">
                  {versions.slice().reverse().map((v) => (
                    <div key={v.id} className="flex items-center gap-2 py-1 border-b border-neutral-50 last:border-0">
                      <span className="text-xs font-bold text-[#0F172A]">{v.label}</span>
                      <span className="text-[10px] text-neutral-400">{fmtDT(v.at)}{v.by ? ` · ${v.by}` : ""}</span>
                      <span className={`ml-auto text-[9px] font-semibold border rounded-full px-2 py-0.5 ${v.status === "envoyee" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-neutral-100 text-neutral-500 border-neutral-200"}`}>{v.status === "envoyee" ? "Envoyée au client" : "Brouillon interne"}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
            <button onClick={() => navigate(`/sites-web/projet/${p.id}`)} className="text-[11px] font-semibold text-[#0369A1] hover:underline" data-testid="wpanel-files-fullpage">
              Gérer les fichiers sur la page projet ›
            </button>
          </>
        )}

        {tab === "commentaires" && (
          <>
            {(p.messages || []).length === 0 && <p className="text-[11px] text-neutral-400" data-testid="wpanel-comments-empty">Aucun commentaire pour le moment.</p>}
            <div className="space-y-2.5">
              {(p.messages || []).slice().reverse().map((m) => (
                <div key={m.id} className="flex items-start gap-2.5" data-testid={`wpanel-comment-${m.id}`}>
                  <span className="w-7 h-7 rounded-full bg-[#DBEAFE] text-[#1D4ED8] text-[9px] font-bold flex items-center justify-center shrink-0">{initialsOf(m.by)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-neutral-400">
                      <b className="text-neutral-700">{m.by}</b> · {relTime(m.at)} ·{" "}
                      <span className={`font-semibold ${m.internal ? "text-amber-700" : "text-blue-700"}`}>{m.internal ? "Interne DGA" : "Visible par le client"}</span>
                    </p>
                    <p className="text-xs text-neutral-600 whitespace-pre-line mt-0.5">{m.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-neutral-200 rounded-xl p-2.5 space-y-2">
              <textarea rows={2} value={msgText} onChange={(e) => setMsgText(e.target.value)} data-testid="wpanel-comment-input"
                placeholder="Écrire un commentaire…" className="w-full text-xs focus:outline-none resize-y" />
              <div className="flex items-center gap-2">
                <button onClick={() => setMsgInternal(true)} data-testid="wpanel-comment-internal"
                  className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${msgInternal ? "bg-amber-100 border-amber-300 text-amber-800" : "border-neutral-200 text-neutral-400"}`}>
                  Interne DGA
                </button>
                <button onClick={() => setMsgInternal(false)} data-testid="wpanel-comment-client"
                  className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${!msgInternal ? "bg-blue-100 border-blue-300 text-blue-800" : "border-neutral-200 text-neutral-400"}`}>
                  Visible par le client
                </button>
                <button onClick={postMessage} disabled={!msgText.trim()} data-testid="wpanel-comment-send"
                  className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold bg-[#0B132B] text-white rounded-lg px-3 py-1.5 hover:bg-[#1C2A4F] disabled:opacity-50">
                  <MessageSquare className="w-3 h-3" /> Publier
                </button>
              </div>
            </div>
          </>
        )}

        {tab === "historique" && (
          <>
            {(p.activity || []).length === 0 && <p className="text-[11px] text-neutral-400">Les actions du projet apparaîtront ici.</p>}
            <div className="space-y-2.5" data-testid="wpanel-activity">
              {(p.activity || []).slice().reverse().map((a, i) => (
                <div key={a.id || i} className="flex items-start gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-[#3B82F6] mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-neutral-700">{a.text || a.message || ""}</p>
                    <p className="text-[10px] text-neutral-400">{fmtDT(a.at)}{a.by ? ` · ${a.by}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
